import { Popup, Button, XIcon, colors, LabeledInput, ArrowIcon, ChevronIcon, LabeledIconInput, LabeledSelect, ConnectedPlusIcon, AlertNotification } from '@commonsku/styles';
import styled from 'styled-components';
import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import PromostandardsBreakdownDetailsDropdown from './PromostandardsBreakdownDetailsDropdown';
import { useSelector } from 'react-redux';
import { getUoms, getUomName, getAvailableCharges, getUomValues } from '../promostandard_utils';
import useContainerScroll, { ScrollContainer } from '../hooks/useContainerScroll';
import useGlobalKeyboardEvent from '../hooks/useGlobalKeyboardEvent';
import { oauth } from '../utils';
import { trackEvent } from '../utils/analytics';
const StyledLabeledIconInput = styled(({ className, iconProps, inputProps, ...props }) => {
  return <LabeledIconInput labelProps={{ className }} className={className} iconProps={iconProps} inputProps={inputProps} {...props} />;
})`
&&& div:hover {
  background-color: #FFFFFF;
}
&&& div {
  background-color: #FFFFFF;
}
&&& input {
  height: auto;
}
`;

const StyledHr = styled.hr`
  width: 100%;
  border: 1px solid ${colors.neutrals[40]};
`;

const StyledSpan = styled.span`
  ${props => props.error && `color: ${colors.errors[50]};`}
  font-size: 12px;
`;

const PopUpHeader = styled.div`
&&& {
  font-size: 24px;
  font-weight: 600;
  line-height: 40px;
  color: ${colors.neutrals[90]};
  display: flex;
  justify-content: space-between;
  align-items: center;
}
`;

const StyledLabeledSelect = styled(LabeledSelect)`
  &&& label {
    line-height: 24px;
  }
  &&& .commonsku-styles-select__control {
    margin-bottom: ${props => props.error || props.hasText ? 0 : '1rem'};
    transition: none;
  }

  &&& .commonsku-styles-select__single-value--is-disabled {
    color: ${colors.neutrals[90]};
    opacity: 1;
  }
`;

// helpers

const normalize = (s) => (s || '').toLowerCase();

const parseLocationTitle = (title) => {
  const match = title.match(/^Location [0-9]*:: /);
  return {
    locationId: match ? title.substring(0, title.lastIndexOf(':: ')) : 'Location',
    baseTitle: match ? title.substring(title.lastIndexOf(':: ') + 3) : title,
  };
};

const getBaseImprint = (location, imprints) =>
  imprints?.[location.artworks?.[0]?.imprint_id]?.imprint_name;

const findMatchingDecoration = (psLocation, imprintName) => {
  if (!imprintName || !psLocation) return { decoration: null, autoSelected: false };
  let decoration = psLocation.decorations.find(
    (d) => normalize(d.decoration_name) === normalize(imprintName)
  );
  if (!decoration && psLocation.decorations.length === 1) {
    return { decoration: psLocation.decorations[0], autoSelected: true };
  }
  return { decoration, autoSelected: !!decoration };
};

const applyImprintToLocationData = (locationData, psLocation, imprintName) => {
  if (!imprintName || !psLocation) return locationData;

  const { decoration, autoSelected } = findMatchingDecoration(psLocation, imprintName);
  if (!decoration) return locationData;

  return {
    ...locationData,
    ps_decoration_id: decoration.ps_decoration_id,
    decoration_uom: decoration.decoration_units_included,
    decoration_auto_selected: autoSelected,
    decoration_units_included_uom: decoration.decoration_units_included_uom,
    decoration_name: decoration.decoration_name,
  };
};

const findAutoPsLocation = ({ psLocations, baseTitle, usedPsLocationIds }) => {
  // strict title match first
  const exact = psLocations.find(
    (loc) => normalize(loc.location_name) === normalize(baseTitle) && !usedPsLocationIds.has(loc.ps_location_id)
  );
  if (exact) return { psLocation: exact, auto: false };

  // fallback: only one option
  if (psLocations.length === 1 && !usedPsLocationIds.has(psLocations[0].ps_location_id)) {
    return { psLocation: psLocations[0], auto: true };
  }

  return { psLocation: null, auto: false };
};

const getDecorationUomText = (decoration, uoms) => {
  if (!decoration) return null;
  const maxUnits = decoration.decoration_units_max || 0;
  const uomArray = uoms[decoration.decoration_units_included_uom] || [];
  const maxValue = Math.max(maxUnits, uomArray.length > 0 ? uomArray[uomArray.length - 1] : maxUnits);
  const hasYUom = uomArray.length > 0;
  const maxYUom = `Max ${maxValue} ${getUomName(decoration.decoration_units_max, decoration.decoration_units_included_uom)}`;
  const uomText = hasYUom
    ? `${decoration.decoration_units_included} ${getUomName(decoration.decoration_units_included, decoration.decoration_units_included_uom)} Included (${maxYUom})`
    : maxYUom;
  return <span style={{ fontSize: 'small' }}>{uomText}</span>;
};

const getFieldText = (error, availableCount, totalCount, autoSelect = false) => {
  if (error) return error;
  if (totalCount === 1 && availableCount === 1) return 'The Supplier offers one option for this field';
  if (autoSelect) return 'Automatically selected';
  return null;
};

const getExcludedChargeIds = (userSelectedCharges, currentChargeId) => {
  return userSelectedCharges
    .filter(charge => !charge.is_multiple && charge.ps_charge_id !== currentChargeId)
    .map(charge => charge.ps_charge_id);
};

const findMatchingCharge = (availableCharges, itemCostTitle) => {
  if (!itemCostTitle || !availableCharges?.length) return null;

  // Try exact match (case-insensitive)
  const normalizedTitle = normalize(itemCostTitle);
  const exactMatch = availableCharges.find(
    (charge) => normalize(charge.description) === normalizedTitle
  );

  return exactMatch || null;
};

const transformBreakdowns = (selectedSkus, item) =>
  item.breakdowns?.map(breakdown => ({
    breakdown_id: breakdown.breakdown_id,
    ps_part_id: selectedSkus[breakdown.breakdown_id]?.ps_part_id,
    ext_sku_id: selectedSkus[breakdown.breakdown_id]?.id,
  })).filter(b => b.ps_part_id) || [];

const transformLocations = (selectedLocations, item) =>
  item.item_locations?.map(location => {
    const selectedLocation = selectedLocations[location.item_location_id];
    if (!selectedLocation?.ps_location_id) return null;
    return {
      item_location_id: location.item_location_id,
      ext_location_id: selectedLocation.ps_location_id,
      ext_decoration_id: selectedLocation.ps_decoration_id,
      item_location_title: selectedLocation.item_location_title,
      decoration_uom: selectedLocation.decoration_uom,
      decoration_units_included_uom: selectedLocation.decoration_units_included_uom,
      decoration_name: selectedLocation.decoration_name,
      charges: selectedLocation.charges?.map(charge => ({
        item_cost_id: charge.item_cost_id,
        ps_charge_id: charge.ps_charge.ps_charge_id,
        description: charge.ps_charge.description,
      })) || []
    };
  }).filter(Boolean) || [];

const useInitialSelectedLocations = ({
  is3rdParty,
  psLocations,
  itemLocations,
  imprints,
  setSelectedLocations,
  selectedLocations,
}) => {
  useEffect(() => {
    if (is3rdParty) return;
    if (!psLocations?.length || !itemLocations?.length) return;
    if (Object.keys(selectedLocations || {}).length) return;

    const usedPsLocationIds = new Set();

    const initialSelections = itemLocations.reduce((acc, loc) => {
      const { baseTitle } = parseLocationTitle(loc.item_location_title);
      const baseImprint = getBaseImprint(loc, imprints);

      const { psLocation, auto } = findAutoPsLocation({
        psLocations,
        baseTitle,
        usedPsLocationIds,
      });

      if (!psLocation) return acc;

      usedPsLocationIds.add(psLocation.ps_location_id);

      const baseData = {
        ps_location_id: psLocation.ps_location_id,
        location_auto_selected: auto,
        item_location_title: psLocation.location_name,
        charges: [],
      };

      acc[loc.item_location_id] = applyImprintToLocationData(baseData, psLocation, baseImprint);
      return acc;
    }, {});

    if (Object.keys(initialSelections).length) {
      setSelectedLocations(initialSelections);
    }
  }, [is3rdParty, psLocations, itemLocations, imprints, setSelectedLocations]);
};

const BreakdownMapping = ({
  breakdown,
  breakdownColors,
  breakdownSizes,
  parts,
  isOpen,
  onToggle,
  selectedSku,
  onSelectSku,
  onTouchField,
  error
}) => {
  const color = breakdownColors.find((color) => color.color_id === breakdown.color_id);
  const size = breakdownSizes.find((size) => size.size_id === breakdown.size_id);

  const labelValue = selectedSku
    ? [selectedSku.color_name, selectedSku.size_name].filter(Boolean).join('/')
    : '';

  const handleToggle = (e) => {
    e.stopPropagation();
    onToggle(breakdown.breakdown_id);
  };

  const handleSelect = useCallback(
    (sku) => onSelectSku(breakdown.breakdown_id, sku),
    [onSelectSku, breakdown.breakdown_id]
  );
  const handleClose = () => {
    onTouchField(`breakdown_${breakdown.breakdown_id}`);
    onToggle(null);
  };

  const hasValidSelection = !!selectedSku?.id;
  const fieldText = error || (hasValidSelection && selectedSku?.auto_selected ? 'Automatically selected' : null);
  const leftLabel = [color?.color_name, size?.size_name].filter(Boolean).join('/');

  return <div style={{ display: 'flex', flexDirection: 'row', gap: 31, alignItems: 'center', position: 'relative' }}>
    <LabeledInput wrapperProps={{ style: { flex: '1 1 0' } }} value={leftLabel} label="Color/Size" disabled />
    <ArrowIcon direction="right" />
    <div style={{ flex: '1 1 0', position: 'relative' }} onClick={handleToggle}>
      {hasValidSelection && (
        <ConnectedPlusIcon style={{ position: 'absolute', left: 12, top: '43px', transform: 'translateY(-50%)', zIndex: 1 }} />
      )}
      <StyledLabeledIconInput
        label="Color/Size"
        Icon={<ChevronIcon style={{ backgroundColor: '#FFFFFF' }} direction={'down'} />}
        iconPosition='right'
        iconProps={{ style: { backgroundColor: '#FFFFFF' } }}
        readOnly={true}
        type='text'
        iconColor='#B8C4CB'
        value={labelValue}
        style={{
          height: '39px',
          background: '#FFFFFF',
          display: 'flex',
          marginBottom: fieldText ? 0 : '1rem',
          borderWidth: 1,
          borderStyle: 'solid',
          borderColor: error ? colors.errors[50] : '#B8C4CB',
          cursor: 'pointer',
          paddingLeft: 32,
        }}
      />
      {fieldText && <StyledSpan error={error}>{fieldText}</StyledSpan>}
    </div>
    <PromostandardsBreakdownDetailsDropdown
      isOpen={isOpen}
      parts={parts}
      initialColor={color?.color_name}
      initialSize={size?.size_name}
      selectedSku={selectedSku}
      onSelect={handleSelect}
      onClose={handleClose}
    />
  </div>
}

const ChargeMapping = ({
  itemCost,
  selectedLocation,
  selectedDecoration,
  userSelectedCharges,
  quirks,
  division_id,
  displayOrder,
  onTouchField,
  onChangeCharge,
  error
}) => {
  const isRunCharge = itemCost.quantity === null;
  const selectedCharge = selectedLocation?.charges?.find(
    charge => charge.item_cost_id === itemCost.item_cost_id
  )?.ps_charge;
  const chargeType = isRunCharge ? 'RUN' : 'SETUP';
  const selectedChargeIds = getExcludedChargeIds(userSelectedCharges, selectedCharge?.ps_charge_id);

  const availableCharges = getAvailableCharges(
    selectedDecoration?.charges ?? [],
    quirks,
    division_id
  )(
    chargeType,
    displayOrder,
    { [selectedDecoration?.decoration_units_included_uom]: [selectedLocation?.decoration_uom] },
    selectedChargeIds
  );

  const nonDividerCharges = availableCharges.filter(charge => !charge.is_divider);

  const chargeOptions = nonDividerCharges.map((charge) => ({
    value: charge.ps_charge_id,
    label: <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <ConnectedPlusIcon /> {charge.description}
    </span>,
  }));

  const chargeLabel = isRunCharge ? 'Run Charge' : 'Fixed Charge';

  // Get total charges of this type (without excluding selected charges)
  const totalChargesOfType = getAvailableCharges(
    selectedDecoration?.charges ?? [],
    quirks,
    division_id
  )(
    chargeType,
    displayOrder,
    { [selectedDecoration?.decoration_units_included_uom]: [selectedLocation?.decoration_uom] },
    [] // No excluded charges to get the total count
  ).filter(charge => !charge.is_divider).length;

  useEffect(() => {
    if (selectedCharge) return;
    if (nonDividerCharges.length === 0) return;

    // auto select exact name match 
    const exactMatch = findMatchingCharge(nonDividerCharges, itemCost.item_cost_title);
    if (exactMatch) {
      onChangeCharge({ ...exactMatch, charge_auto_selected: true }, itemCost.item_cost_id);
      return;
    }

    // auto select if only one charge of this type is available
    if (totalChargesOfType === 1 && nonDividerCharges.length === 1) {
      onChangeCharge({ ...nonDividerCharges[0], charge_auto_selected: true }, itemCost.item_cost_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    itemCost.item_cost_id,
    itemCost.item_cost_title,
    selectedLocation?.decoration_uom,
    totalChargesOfType,
  ]);

  const handleChange = (value) => {
    if (!value) {
      onChangeCharge(null, itemCost.item_cost_id);
      return;
    }
    const charge = availableCharges.find(c => c.ps_charge_id === value.value);
    if (charge) {
      onChangeCharge({ ...charge, charge_auto_selected: false }, itemCost.item_cost_id);
    }
  };

  const isAutoSelected = selectedCharge?.charge_auto_selected || false;
  const fieldText = getFieldText(error, chargeOptions.length, totalChargesOfType, isAutoSelected);

  return (
    <div style={{ display: 'flex', flexDirection: 'row', gap: 31, position: 'relative' }}>
      <LabeledInput
        wrapperProps={{ style: { flex: '1 1 0' } }}
        value={itemCost.item_cost_title}
        label={chargeLabel}
        disabled
      />
      <div style={{ height: 90, alignContent: 'center' }}>
        <ArrowIcon direction="right" />
      </div>
      <div style={{ flex: '1 1 0', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {chargeOptions.length > 0 ? (
          <div>
            <StyledLabeledSelect
              label={chargeLabel}
              labelProps={{ style: { lineHeight: '24px' } }}
              options={chargeOptions}
              isSearchable={false}
              value={chargeOptions.find(o => o.value === selectedCharge?.ps_charge_id) || null}
              onChange={handleChange}
              onBlur={() => onTouchField(`charge_${itemCost.item_cost_id}`)}
              error={error}
              isDisabled={totalChargesOfType === 1}
              isClearable
              hasText={!!fieldText}
            />
            {fieldText && <StyledSpan error={error}>{fieldText}</StyledSpan>}
            {selectedCharge?.is_editable && (
              <LabeledInput
                label="Custom Charge Name (Optional)"
                labelProps={{ style: { lineHeight: '24px' } }}
                type="text"
                value={selectedCharge?.description || ''}
                onChange={e => onChangeCharge(
                  { ...selectedCharge, description: e.target.value },
                  itemCost.item_cost_id
                )}
                onBlur={() => onTouchField(`charge_${itemCost.item_cost_id}`)}
              />
            )}
          </div>
        ) : (
          <AlertNotification alertType="warn">
            {totalChargesOfType > 0
              ? `All available ${isRunCharge ? 'run' : 'fixed'} charges for this location are selected`
              : 'This product does not support custom charges.'}
          </AlertNotification>
        )}
      </div>
    </div>
  );
};

const LocationDecorationMapping = ({
  location,
  psLocations,
  imprints,
  selectedLocations,
  onSelectLocation,
  onTouchField,
  division_id,
  itemCosts,
  quirks,
  errors = {}
}) => {
  const selectedLocation = selectedLocations[location.item_location_id];
  const { locationId, baseTitle } = parseLocationTitle(location.item_location_title);

  const baseImprint = getBaseImprint(location, imprints);
  const userSelectedCharges = selectedLocation?.charges?.map(charge => charge.ps_charge) || [];

  const selectedPsLocation = psLocations.find((loc) => loc.ps_location_id === selectedLocation?.ps_location_id);

  const selectedDecoration = selectedPsLocation?.decorations.find((d) => d.ps_decoration_id === selectedLocation?.ps_decoration_id);

  const uoms = selectedDecoration ? getUoms(selectedDecoration) : {};

  const maxDecorationUom = useMemo(() => {
    if (!selectedDecoration) return 0;
    const uomArray = uoms[selectedDecoration.decoration_units_included_uom] || [];
    const lastUomValue = uomArray.length > 0 ? uomArray[uomArray.length - 1] : selectedDecoration.decoration_units_max;
    return Math.max(selectedDecoration.decoration_units_max || 0, lastUomValue || 0);
  }, [selectedDecoration, uoms]);

  const locationOptions =
    psLocations
      .filter((loc) => {
        if (loc.ps_location_id === selectedLocation?.ps_location_id) return true;
        const isSelectedElsewhere = Object.values(selectedLocations || {}).some(
          (sl) => sl?.ps_location_id === loc.ps_location_id
        );
        return !isSelectedElsewhere;
      })
      .map((loc) => ({
        value: loc.ps_location_id,
        label: <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><ConnectedPlusIcon /> {loc.location_name}</span>,
      }));

  useEffect(() => {
    if (psLocations.length === 1 && locationOptions.length === 1 && !selectedLocation?.ps_location_id) {
      const remainingLocation = psLocations.find(
        loc => loc.ps_location_id === locationOptions[0].value
      );
      if (remainingLocation) {
        const baseData = {
          ps_location_id: remainingLocation.ps_location_id,
          charges: [],
          location_auto_selected: true,
          item_location_title: remainingLocation.location_name
        };
        const locationData = applyImprintToLocationData(baseData, remainingLocation, baseImprint);
        onSelectLocation(location.item_location_id, locationData);
      }
    }
  }, [locationOptions.length, selectedLocation?.ps_location_id, location.item_location_id, psLocations, baseImprint, onSelectLocation]);

  const decorationOptions = (selectedPsLocation?.decorations || []).map((decoration) => ({
    value: decoration.ps_decoration_id,
    label: <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><ConnectedPlusIcon /> {decoration.decoration_name}</span>,
  }));

  const handleChangeLocation = (value) => {
    if (!value) {
      onSelectLocation(location.item_location_id, {
        ps_location_id: null,
        charges: [],
        location_auto_selected: false,
        item_location_title: null
      });
      return;
    }

    if (selectedPsLocation?.ps_location_id === value.value) return;

    const newPsLocation = psLocations.find(loc => loc.ps_location_id === value.value);
    if (!newPsLocation) return;

    const baseData = {
      ps_location_id: value.value,
      charges: selectedLocation?.charges ?? [],
      location_auto_selected: false,
      item_location_title: newPsLocation.location_name
    };

    const locationData = applyImprintToLocationData(baseData, newPsLocation, baseImprint);

    onSelectLocation(location.item_location_id, locationData);
  };

  const handleChangeDecoration = (value) => {
    if (selectedDecoration?.ps_decoration_id === value.value) return;
    const targetDecoration = selectedPsLocation?.decorations.find(
      (d) => d.ps_decoration_id === value.value
    );
    onSelectLocation(location.item_location_id, {
      ...selectedLocation,
      ps_decoration_id: value.value,
      decoration_uom: targetDecoration?.decoration_units_included,
      decoration_units_included_uom: targetDecoration?.decoration_units_included_uom,
      decoration_name: targetDecoration?.decoration_name,
      decoration_auto_selected: false
    });
  };

  const handleChangeUom = (e) => {
    const targetValue = Math.max(0, Math.min(Number(e.target.value), maxDecorationUom));
    onSelectLocation(location.item_location_id, { ...selectedLocation, decoration_uom: targetValue });
  };

  const handleChangeCharge = (value, itemCostId) => {
    onSelectLocation(location.item_location_id, (current) => {
      const existingCharges = current?.charges ?? [];
      const updatedCharges = existingCharges.filter(charge => charge.item_cost_id !== itemCostId);
      if (value) {
        updatedCharges.push({ item_cost_id: itemCostId, ps_charge: value });
      }
      return { ...current, charges: updatedCharges };
    });
  };

  const visibleUomKeys = Object.keys(uoms).filter(
    u => uoms[u].length > 0 && uoms[u][uoms[u].length - 1] > 0
  );

  const locationFieldText = getFieldText(
    errors.location,
    locationOptions?.length,
    psLocations?.length,
    selectedLocation?.location_auto_selected
  );
  const decorationFieldText = getFieldText(
    errors.decoration,
    decorationOptions?.length,
    selectedPsLocation?.decorations?.length,
    selectedLocation?.decoration_auto_selected
  );

  const hasNoOptions = locationOptions.length === 0 && !selectedLocation?.ps_location_id;
  const hasNoDecorationOptions =
    selectedLocation?.ps_location_id &&
    Boolean(location.artworks?.[0]) &&
    decorationOptions.length === 0;
  if (hasNoOptions) {
    return <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', flexDirection: 'row', gap: 31, alignItems: 'center', position: 'relative' }}>
        <LabeledInput wrapperProps={{ style: { flex: '1 1 0' } }} value={baseTitle} label={locationId} disabled />
        <ArrowIcon direction="right" />
        <div style={{ flex: '1 1 0' }}>
          <AlertNotification alertType="warn">
            All available decoration locations for this product are selected
          </AlertNotification>
        </div>
      </div>
    </div>;
  }

  return <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
    <div style={{ display: 'flex', flexDirection: 'row', gap: 31, alignItems: 'center', position: 'relative' }}>
      <LabeledInput wrapperProps={{ style: { flex: '1 1 0' } }} value={baseTitle} label={locationId} disabled />
      <ArrowIcon direction="right" />
      <div style={{ flex: '1 1 0' }}>
        <StyledLabeledSelect
          label="Location"
          labelProps={{ style: { lineHeight: '24px' } }}
          isSearchable={false}
          options={locationOptions}
          value={locationOptions.find(o => o.value === selectedPsLocation?.ps_location_id)}
          onChange={handleChangeLocation}
          onBlur={() => onTouchField(`location_${location.item_location_id}`)}
          isClearable
          isDisabled={psLocations?.length === 1}
          error={errors.location}
          hasText={!!locationFieldText}
        />
        {
          locationFieldText &&
          <StyledSpan error={errors.location}>{locationFieldText}</StyledSpan>
        }
      </div>
    </div>
    {
      selectedLocation?.ps_location_id && location.artworks?.[0]
        ? <div style={{ display: 'flex', flexDirection: 'row', gap: 31, position: 'relative' }}>
          <LabeledInput wrapperProps={{ style: { flex: '1 1 0' } }} value={baseImprint} label="Imprint Type" disabled />
          <div style={{ height: 90, alignContent: 'center' }}>
            <ArrowIcon direction="right" />
          </div>
          <div style={{ flex: '1 1 0', display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {hasNoDecorationOptions ? (
                <AlertNotification alertType="warn">
                  Timeout: Please refresh
                </AlertNotification>
              ) : (
                <>
                  <StyledLabeledSelect
                    label="Imprint Type"
                    labelProps={{ style: { lineHeight: '24px' } }}
                    options={decorationOptions}
                    // is only one decoration disable select
                    isDisabled={decorationOptions.length === 1}
                    value={decorationOptions.find(o => o.value === selectedLocation?.ps_decoration_id) || null}
                    onChange={handleChangeDecoration}
                    onBlur={() => onTouchField(`decoration_${location.item_location_id}`)}
                    error={errors.decoration}
                    hasText={visibleUomKeys.length > 0 || !!decorationFieldText}
                  />
                  {getDecorationUomText(selectedDecoration, uoms)}
                  {decorationFieldText && <StyledSpan error={errors.decoration}>{decorationFieldText}</StyledSpan>}
                </>
              )}
            </div>
            {
              !hasNoDecorationOptions && visibleUomKeys.map(u =>
                <div key={u} style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                  <div style={{ flex: '1 1 0' }}>
                    <LabeledInput
                      label={`# of ${u}`}
                      type="number"
                      min={0}
                      max={maxDecorationUom}
                      style={{ marginBottom: errors.uom ? 0 : '1rem' }}
                      value={String(selectedLocation?.decoration_uom ?? selectedDecoration?.decoration_units_included)}
                      onChange={handleChangeUom}
                      error={errors.uom}
                    />
                    {errors.uom && <StyledSpan error>{errors.uom}</StyledSpan>}
                  </div>
                </div>
              )
            }
          </div>
        </div>
        : null
    }
    {
      selectedLocation?.ps_location_id && itemCosts
        .map((itemCost) => (
          <ChargeMapping
            key={itemCost.item_cost_id}
            itemCost={itemCost}
            selectedLocation={selectedLocation}
            selectedDecoration={selectedDecoration}
            userSelectedCharges={userSelectedCharges}
            quirks={quirks}
            division_id={division_id}
            displayOrder={location.display_order}
            onChangeCharge={handleChangeCharge}
            onTouchField={onTouchField}
            error={errors.charges?.[itemCost.item_cost_id]}
          />
        ))
    }
  </div>
}

export const queueUpgradePromoStandardsProduct = async (
  item_id,
  ext_product_id,
  ps_product_id,
  promo_api_name,
  breakdowns,
  locations
) => {
  const data = {
    actionName: 'queue-upgrade-ps-product',
    item_id,
    ext_product_id,
    ps_product_id,
    promo_api_name,
    breakdowns,
    locations
  };

  const { json } = await oauth('POST', 'item', data);
  return json;
};

const UpgradeProductPopup = ({
  onClose,
  onQueued,
  item,
  product,
  onPublishWithoutUpgrade,
  unavailableFailureMessage = 'Sorry, upgrade is unavailable at this time. Please try again later.',
}) => {
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [selectedSkus, setSelectedSkus] = useState({});
  const [selectedLocations, setSelectedLocations] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [upgradeSuccess, setUpgradeSuccess] = useState(false);
  const [upgradeError, setUpgradeError] = useState(false);
  const [hasRetriedUpgrade, setHasRetriedUpgrade] = useState(false);

  useGlobalKeyboardEvent('Escape', onClose);

  const imprints = useSelector((state) => state.entities.imprints);
  const quirks = useSelector((state) => state.entities.promostandards_quirks);

  const psLocations = product.locations || [];
  const itemLocations = item.item_locations || [];
  const is3rdParty = !!item.is_3rd_party_decorator;

  const itemCostsByLocation = useMemo(() => {
    const map = new Map();
    (item.item_costs || []).forEach((c) => {
      const arr = map.get(c.item_location_id) || [];
      arr.push(c);
      map.set(c.item_location_id, arr);
    });
    return map;
  }, [item.item_costs]);

  useInitialSelectedLocations({
    is3rdParty,
    psLocations,
    itemLocations,
    imprints,
    setSelectedLocations,
    selectedLocations,
  });

  const shouldShowError = (key) => hasAttemptedSubmit || touchedFields[key];
  const getError = (key) => (shouldShowError(key) ? validationErrors[key] : null);

  const validateForm = () => {
    const errors = {};

    // 1. Validate Breakdowns (SKU mappings)
    item.breakdowns?.forEach((breakdown) => {
      if (!selectedSkus[breakdown.breakdown_id]) {
        errors[`breakdown_${breakdown.breakdown_id}`] = 'Please select a Color/Size';
      }
    });

    // 2. Validate Locations, Decorations, UOMs, and Charges
    if (!is3rdParty) {
      (itemLocations || []).forEach((location) => {
        const selected = selectedLocations[location.item_location_id];

        // Check available PS locations for this item location
        const availablePsLocations = psLocations.filter((loc) => {
          if (loc.ps_location_id === selected?.ps_location_id) return true;
          return !Object.values(selectedLocations || {}).some(
            (sl) => sl?.ps_location_id === loc.ps_location_id
          );
        });

        // Skip validation if no PS locations are available (supplier doesn't have enough locations)
        if (availablePsLocations.length === 0 && !selected?.ps_location_id) return;

        // Location is required
        if (!selected?.ps_location_id) {
          errors[`location_${location.item_location_id}`] = 'Please select a Location';
          return; // Can't validate further without location
        }

        const psLocation = psLocations.find(
          loc => loc.ps_location_id === selected.ps_location_id
        );

        // Decoration is required if artwork exists
        const hasArtwork = !!location.artworks?.[0];
        if (hasArtwork && !selected?.ps_decoration_id) {
          errors[`decoration_${location.item_location_id}`] = 'Please select an Imprint Type';
        }

        const selectedDecoration = psLocation?.decorations.find(
          d => d.ps_decoration_id === selected.ps_decoration_id
        );

        // UOM is required if there are visible UOM keys
        if (selectedDecoration) {
          const uoms = getUoms(selectedDecoration);
          const visibleUomKeys = Object.keys(uoms).filter(
            u => uoms[u].length > 0 && uoms[u][uoms[u].length - 1] > 0
          );

          if (visibleUomKeys.length > 0 && (selected?.decoration_uom == null)) {
            errors[`uom_${location.item_location_id}`] = 'Please enter UOM value';
          }
        }

        // 3. Validate Charges (always validate since they're visible regardless of decoration)
        const itemCosts = itemCostsByLocation.get(location.item_location_id) || [];
        const userSelectedCharges = selected?.charges?.map(charge => charge.ps_charge) || [];

        itemCosts.forEach((itemCost) => {
          const isRunCharge = itemCost.quantity === null;
          const chargeType = isRunCharge ? 'RUN' : 'SETUP';
          const selectedCharge = selected?.charges?.find(
            charge => charge.item_cost_id === itemCost.item_cost_id
          )?.ps_charge;

          const selectedChargeIds = getExcludedChargeIds(userSelectedCharges, selectedCharge?.ps_charge_id);

          // Calculate available charges
          const availableCharges = getAvailableCharges(
            selectedDecoration?.charges ?? [],
            quirks,
            item.division_id
          )(
            chargeType,
            location.display_order,
            getUomValues(selected),
            selectedChargeIds
          );

          const chargeOptions = availableCharges.filter(charge => !charge.is_divider);

          // Only require charge if options are available
          if (chargeOptions.length > 0 && !selectedCharge) {
            errors[`charge_${itemCost.item_cost_id}`] = 'Please select a charge';
          }
        });
      });
    }

    return errors;
  };

  useEffect(() => {
    if (!Object.keys(touchedFields).length) return;
    setValidationErrors(validateForm());
  }, [
    touchedFields,
    selectedSkus,
    selectedLocations,
    quirks,
    itemLocations,
    is3rdParty,
    itemCostsByLocation,
    item.breakdowns,
    item.division_id,
    psLocations,
  ]);

  const handleToggleDropdown = (breakdownId) => {
    setOpenDropdownId(prev => prev === breakdownId ? null : breakdownId);
  };

  const handleSelectSku = useCallback((breakdownId, sku) => {
    setSelectedSkus(prev => ({
      ...prev,
      [breakdownId]: sku
    }));
  }, []);

  const handleSelectLocation = useCallback((locationId, locationOrUpdater) => {
    setSelectedLocations(prev => ({
      ...prev,
      [locationId]: typeof locationOrUpdater === 'function'
        ? locationOrUpdater(prev[locationId])
        : locationOrUpdater
    }));
  }, []);

  const handleTouchField = useCallback((fieldKey) => {
    setTouchedFields(prev => ({ ...prev, [fieldKey]: true }));
  }, []);

  const handleUpgrade = async () => {
    setHasAttemptedSubmit(true);
    const errors = validateForm();
    setValidationErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }
    const eventName = 'Upgrade item for ePO';
    const eventData = {
      action: 'submit',
      item_id: item.item_id,
      division_name: item.division_name,
    };
    trackEvent(eventName, eventData);

    // Call API
    setIsSubmitting(true);
    try {
      await queueUpgradePromoStandardsProduct(
        item.item_id,
        product.ext_product_id,
        product.ps_product_id,
        product.promo_api_name,
        transformBreakdowns(selectedSkus, item),
        transformLocations(selectedLocations, item)
      );
      onQueued?.({ order_id: item.order_id, item_id: item.item_id });
      setUpgradeSuccess(true);
    } catch (error) {
      const isRetry = upgradeError;
      if (isRetry) setHasRetriedUpgrade(true);
      setUpgradeError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const containerRef = useRef(null);
  const { canScrollDown, canScrollUp, scrollDown, scrollUp } = useContainerScroll(
    containerRef,
    [
      item.breakdowns?.length,
      item.item_locations?.length,
      upgradeSuccess,
      upgradeError,
      hasAttemptedSubmit
    ]
  );

  const header = () => (
    <PopUpHeader>
      <div>
        Upgrade Product
      </div>
      <XIcon size="large" pointer onClick={onClose} />
    </PopUpHeader>
  );

  const getLocationErrors = (locationId, itemCosts) => {
    const chargeErrors = {};
    itemCosts
      .forEach(cost => {
        const error = getError(`charge_${cost.item_cost_id}`);
        if (error) chargeErrors[cost.item_cost_id] = error;
      });

    return {
      location: getError(`location_${locationId}`),
      decoration: getError(`decoration_${locationId}`),
      uom: getError(`uom_${locationId}`),
      charges: chargeErrors
    };
  };

  const renderScrollContainers = useCallback(() => {
    if (!containerRef.current || upgradeSuccess || upgradeError) {
      return null;
    }

    const scrollContainerProps = (isUp = true) => ({
      isUp,
      onClick: isUp ? scrollUp : scrollDown,
      canScroll: isUp ? canScrollUp() : canScrollDown(),
      width: '100%',
      zIndex: 99999,
      top: 'auto',
      bottom: isUp ? 'auto' : -34,
      left: 0,
      right: 0,
      margin: 0,
      style: { position: 'absolute', cursor: 'pointer', height: '96px' },
      arrowSize: 'large',
      padding: '10px 0',
    });

    return (
      <>
        <ScrollContainer {...scrollContainerProps(true)} />
        <ScrollContainer {...scrollContainerProps(false)} />
      </>
    );
  }, [canScrollDown, canScrollUp, scrollDown, scrollUp, upgradeSuccess, upgradeError]);

  const renderBody = () => {
    if (upgradeSuccess) {
      return <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, gap: 16, overflowX: 'hidden' }}>
        <AlertNotification alertType="success">
          Item upgrade in progress.
        </AlertNotification>
      </div>
    }

    if (upgradeError) {
      const errorMessage = hasRetriedUpgrade ? unavailableFailureMessage : 'Item upgrade failed.';
      return <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, gap: 16, overflowX: 'hidden' }}>
        <AlertNotification alertType="error">
          {errorMessage}
        </AlertNotification>
      </div>
    }

    return (
      <div style={{
        position: 'relative',
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0
      }}>
        <div
          ref={containerRef}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            overflowY: 'auto',
            overflowX: 'hidden',
            flex: 1,
            paddingRight: 8,
            paddingBottom: 32,
          }}
        >
          {item.breakdowns?.map((breakdown) => (
            <BreakdownMapping
              key={breakdown.breakdown_id}
              breakdown={breakdown}
              breakdownColors={item.colors}
              breakdownSizes={item.sizes}
              parts={product.parts}
              isOpen={openDropdownId === breakdown.breakdown_id}
              onToggle={handleToggleDropdown}
              selectedSku={selectedSkus[breakdown.breakdown_id]}
              onSelectSku={handleSelectSku}
              onTouchField={handleTouchField}
              error={getError(`breakdown_${breakdown.breakdown_id}`)}
            />
          ))}

          {!item.is_3rd_party_decorator && item.item_locations?.length > 0 && (
            <>
              <StyledHr />
              {item.item_locations?.map((location, index) => {
                const itemCosts = itemCostsByLocation.get(location.item_location_id) || [];
                return (
                  <div key={location.item_location_id}>
                    <LocationDecorationMapping
                      quirks={quirks}
                      location={location}
                      psLocations={psLocations}
                      selectedLocations={selectedLocations}
                      onSelectLocation={handleSelectLocation}
                      onTouchField={handleTouchField}
                      imprints={imprints}
                      division_id={item.division_id}
                      itemCosts={itemCosts}
                      errors={getLocationErrors(location.item_location_id, itemCosts)}
                    />
                    {index !== item.item_locations.length - 1 && (
                      <StyledHr />
                    )}
                  </div>
                );
              })}
            </>
          )}
        </div>
        {renderScrollContainers()}
      </div>
    );
  }

  const renderActions = () => {
    const actionsStyle = { display: 'flex', alignItems: 'end', justifyContent: 'end', gap: 8 };

    if (hasRetriedUpgrade && onPublishWithoutUpgrade) {
      return (
        <div style={actionsStyle}>
          <Button variant="text" size="medium" onClick={onClose}>Cancel</Button>
          <Button size="medium" onClick={onPublishWithoutUpgrade}>Publish without Upgrading</Button>
        </div>
      );
    }

    if (upgradeSuccess || hasRetriedUpgrade) {
      return (
        <div style={actionsStyle}>
          <Button size="medium" onClick={onClose}>Done</Button>
        </div>
      );
    }

    const isRetrying = upgradeError && !hasRetriedUpgrade;
    const buttonText = isSubmitting
      ? (isRetrying ? 'Retrying...' : 'Upgrading...')
      : (isRetrying ? 'Retry' : 'Upgrade');

    return (
      <div style={actionsStyle}>
        <Button variant="text" size="medium" onClick={onClose}>Cancel</Button>
        <Button size="medium" onClick={handleUpgrade} disabled={isSubmitting}>
          {buttonText}
        </Button>
      </div>
    );
  };

  const popupHeight = upgradeSuccess || upgradeError ? 'auto' : '984px';
  const popupWidth = upgradeSuccess || upgradeError ? '576px' : '750px';

  return <Popup
    header={header()}
    width={popupWidth}
    height={popupHeight}
    style={{ padding: 16, gap: 16, borderRadius: 10, display: 'flex', flexDirection: 'column', maxHeight: 'calc(100vh - 32px)' }}
    popupContentStyle={{ display: 'flex', flexDirection: 'column', gap: 16, flex: 1, minHeight: 0 }}
  >
    {renderBody()}
    {renderActions()}
  </Popup>
}

export default UpgradeProductPopup;